import React, { useEffect, useState } from "react";
import { Bell, Plus, Trash2, Send, Clock, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../ui/dialog";
import { getNotifySettings, updateNotifySettings, sendNotificationNow } from "../../api";

export default function NotifySettingsDialog() {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [recipients, setRecipients] = useState([""]);
  const [times, setTimes] = useState(["12:00"]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const s = await getNotifySettings();
      setEnabled(s.enabled);
      setRecipients(s.recipients?.length ? s.recipients : [""]);
      setTimes(s.times?.length ? s.times : ["12:00"]);
    } catch {
      toast.error("載入設定失敗");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const setRecipient = (i, v) => setRecipients((a) => a.map((x, idx) => (idx === i ? v : x)));
  const setTime = (i, v) => setTimes((a) => a.map((x, idx) => (idx === i ? v : x)));

  const save = async () => {
    const cleanR = recipients.map((r) => r.trim()).filter(Boolean);
    const cleanT = times.map((t) => t.trim()).filter(Boolean);
    if (enabled && !cleanR.length) return toast.error("請至少填一個收件人");
    if (enabled && !cleanT.length) return toast.error("請至少設定一個寄送時間");
    setSaving(true);
    try {
      const res = await updateNotifySettings({ enabled, recipients: cleanR, times: cleanT });
      setRecipients(res.recipients?.length ? res.recipients : [""]);
      setTimes(res.times?.length ? res.times : ["12:00"]);
      toast.success("通知設定已儲存");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "儲存失敗");
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await sendNotificationNow();
      toast.success(`已寄出（含 ${res.count} 份問卷資料）`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "寄送失敗");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="notify-settings-trigger">
          <Bell className="mr-1 h-4 w-4" /> 通知設定
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>名單通知信設定</DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">載入中…</p>
        ) : (
          <div className="space-y-5">
            {/* 開關 */}
            <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/40 px-4 py-3">
              <div>
                <p className="text-sm font-bold">啟用每日名單通知信</p>
                <p className="text-xs text-muted-foreground">關閉後將不會自動寄送</p>
              </div>
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="notify-enabled-switch" />
            </div>

            {/* 收件人 */}
            <div>
              <Label className="mb-2 flex items-center gap-1.5 text-sm font-bold">
                <Mail className="h-4 w-4 text-brand" /> 寄給誰（收件人）
              </Label>
              <div className="space-y-2">
                {recipients.map((r, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      type="email"
                      value={r}
                      onChange={(e) => setRecipient(i, e.target.value)}
                      placeholder="name@lakeze.com"
                      data-testid={`notify-recipient-${i}`}
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setRecipients((a) => (a.length > 1 ? a.filter((_, idx) => idx !== i) : a))}
                      data-testid={`notify-recipient-remove-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                variant="ghost" size="sm" className="mt-2 text-brand"
                onClick={() => setRecipients((a) => [...a, ""])}
                data-testid="notify-add-recipient"
              >
                <Plus className="mr-1 h-4 w-4" /> 新增收件人
              </Button>
            </div>

            {/* 寄送時間 / 次數 */}
            <div>
              <Label className="mb-2 flex items-center gap-1.5 text-sm font-bold">
                <Clock className="h-4 w-4 text-brand" /> 什麼時候寄（每天，可設多個時間）
              </Label>
              <div className="flex flex-wrap gap-2">
                {times.map((t, i) => (
                  <div key={i} className="flex items-center gap-1 rounded-lg border border-border px-2 py-1">
                    <Input
                      type="time"
                      value={t}
                      onChange={(e) => setTime(i, e.target.value)}
                      className="h-9 w-28 border-0 p-0 text-base focus-visible:ring-0"
                      data-testid={`notify-time-${i}`}
                    />
                    <button
                      onClick={() => setTimes((a) => (a.length > 1 ? a.filter((_, idx) => idx !== i) : a))}
                      className="text-muted-foreground hover:text-red-500"
                      data-testid={`notify-time-remove-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <Button
                variant="ghost" size="sm" className="mt-2 text-brand"
                onClick={() => setTimes((a) => [...a, "09:00"])}
                data-testid="notify-add-time"
              >
                <Plus className="mr-1 h-4 w-4" /> 新增寄送時間
              </Button>
              <p className="mt-1 text-xs text-muted-foreground">共 {times.length} 次 / 天 · 時間為台北時間</p>
            </div>
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <Button variant="outline" onClick={sendNow} disabled={sending} data-testid="notify-send-now">
            <Send className="mr-1 h-4 w-4" /> {sending ? "寄送中…" : "立即寄一次"}
          </Button>
          <Button onClick={save} disabled={saving} className="bg-brand text-white hover:bg-[#EA580C]" data-testid="notify-save">
            {saving ? "儲存中…" : "儲存設定"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
